import { useSearchParams } from 'next/navigation';
import { createRef, FC, FormEventHandler, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, Grid, Typography, useTheme } from '@mui/material';
import { useAuthStore, useConfigStore } from '@/store';
import { useRouter, useHasHydrated, useRedirectUrl } from '@/hooks';
import { getRedirectURLFromStatus } from '@/utils/auth/getRedirectURLFromStatus';
import { LOGIN_PHRASES } from '@/consts/phrases/loginPhrases';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { authService } from '@/services/api';
import { ErrorAlert } from '@/components/forms/alerts/ErrorAlert/ErrorAlert';
import { CenteredContentWrapper } from '@/components/layout/CenteredContentWrapper/CenteredContentWrapper';
import { createQueryString } from '@/utils/fp/route/queryParams';

import { AuthStatus } from '@/types/enums/auth/AuthStatus';
import { LOGIN_ERROR_PHRASES } from '@/consts/phrases/errorPhrases';
import { FetchErrorsType, ICountryData } from '@/types';

import styles from '@/styles/modules/Auth.module.css';

interface ReCaptchaProps {
    isDisabled?: boolean;
    countriesDict: ICountryData[] | undefined;
}

export const ReCaptchaForm: FC<ReCaptchaProps> = ({ isDisabled = false, countriesDict }) => {
    const hasHydrated = useHasHydrated();
    const router = useRouter();
    const theme = useTheme();
    const searchParams = useSearchParams();

    const redirectUrl = useRedirectUrl();
    const { t } = usePhrase();

    const [error, setError] = useState<FetchErrorsType>();

    const recaptchaKey = useConfigStore((state) => state.website_google_recaptcha_v2_site_key);

    const { checkCaptcha } = useAuthStore((state) => ({
        checkCaptcha: state.checkCaptcha,
    }));

    const recaptchaRef = createRef<ReCAPTCHA>();

    const handleSubmit: FormEventHandler<HTMLFormElement> = (evt) => {
        evt.preventDefault();
        const checkCaptchaCodeAndCompleteLogin = async () => {
            if (recaptchaRef && recaptchaRef.current) {
                const captchaCode = recaptchaRef.current.getValue();
                if (!captchaCode || captchaCode === '' || typeof captchaCode !== 'string') {
                    return recaptchaRef.current.reset();
                }
                const res = await checkCaptcha(captchaCode, countriesDict);
                if (res.type === 'error') {
                    setError(res.error);
                } else if (typeof res.message === 'string' && res.messagePhraseId) {
                    const queryString = createQueryString(
                        [
                            {
                                name: 'redirectUrl',
                                value: getRedirectURLFromStatus(
                                    res.message,
                                    res.destinationUrl ?? redirectUrl,
                                ),
                            },
                            {
                                name: 'messagePhraseId',
                                value: res.messagePhraseId,
                            },
                        ],
                        searchParams,
                    );
                    router.push(`/login/message?${queryString}`);
                } else {
                    const methods = await authService.get2faMethods();
                    if (methods.length === 1 && res.message !== AuthStatus.Complete) {
                        router.push(getRedirectURLFromStatus('2fa-submit', redirectUrl));
                    } else if (typeof res.message === 'string') {
                        if (!res.hasUidRedirect) {
                            router.push(getRedirectURLFromStatus(res.message, redirectUrl));
                        }
                    }
                }
            }
        };
        void checkCaptchaCodeAndCompleteLogin();
    };

    return (
        <form onSubmit={handleSubmit} className={styles.auth}>
            <CenteredContentWrapper>
                <Typography variant="h5" align="center" className={styles.subtitle}>
                    {t(LOGIN_PHRASES.CONFIRM_NOT_ROBOT)}
                </Typography>
                <Grid container spacing={1}>
                    {hasHydrated && error && (
                        <Grid size={12}>
                            <ErrorAlert id="recaptcha-error" errorMessage={error} sx={{ mb: 1 }} />
                        </Grid>
                    )}
                    <Grid size={12}>
                        <p>{t(LOGIN_PHRASES.ENTER_RECAPTCHA)}</p>
                    </Grid>
                    <Grid size={12} sx={{ mb: 2 }}>
                        {hasHydrated && (
                            <>
                                {recaptchaKey ? (
                                    <ReCAPTCHA
                                        data-testid="recaptcha"
                                        ref={recaptchaRef}
                                        sitekey={recaptchaKey}
                                        className="google-recaptcha"
                                    />
                                ) : (
                                    <Typography color={theme.palette.error.main}>
                                        {t(LOGIN_ERROR_PHRASES.RECAPTCHA_KEY_MISSING)}
                                    </Typography>
                                )}
                            </>
                        )}
                    </Grid>
                    <Grid size={12}>
                        <Button
                            sx={{ width: '100%' }}
                            id="submit-recaptcha"
                            type="submit"
                            variant="contained"
                            disabled={isDisabled}
                        >
                            {t(LOGIN_PHRASES.CONFIRM_LOGIN)}
                        </Button>
                    </Grid>
                </Grid>
            </CenteredContentWrapper>
        </form>
    );
};
